import type { AxiosInstance } from 'axios';
import axios from 'axios';
import { useEffect } from 'react';
import type { QueryClient, UseQueryResult, DefaultError } from '@tanstack/react-query';
import {
  useInfiniteQuery,
  useQueries,
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { useApiClient } from '../clients';
import { queryKeys } from '../lib/query-keys';
import {
  getCoursesBasePath,
  getCoursesPath,
  getCourseExistsPath,
  getCoursePlannerGetCoursesPathQa,
} from '../utils/api-paths';
import {
  isEmbeddedInSucceed,
  isSchoolUser,
  getHighschoolId,
  isCourseCatalogExportAsyncEnabled,
} from '../utils/cp-context';
import type {
  Course,
  CourseDetail,
  CourseFilters,
  CoursesResponse,
  CourseSection,
  LegacyCourse,
  LegacyCoursesResponse,
} from '../types';
import { CourseStatus } from '../types';
import { MOCK_COURSE_CATALOG } from '../mock-data/course-catalog';
import { isCourseCatalogWafDescriptionEncodingEnabled } from '../utils/cp-context';
import { encodeWafSafeDescriptionsInRequestBody } from '../utils/waf-safe-description';
import { isLocalHostRuntime } from '../utils/env.utils';

/**
 * Repository for course catalog data operations.
 * Follows the same pattern as subject-areas-repository.
 * Uses POST /courses/getCourses for fetching courses (matches course-planner-service API).
 */

/**
 * Legacy hook for student features that use the old Course type structure.
 * When VITE_USE_MOCKS=true: returns mock data (courses.json). Otherwise fetches from the real courses endpoint.
 */
export function useLegacyGetCourses(filters?: {
  search?: string;
}): UseQueryResult<LegacyCoursesResponse, DefaultError> {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.courses.list(filters),
    queryFn: async (): Promise<LegacyCoursesResponse> => {
      if (isMockMode()) {
        const mockData = await import('../mock-data/courses.json');
        let courses = mockData.default.courses as LegacyCourse[];

        if (filters?.search) {
          const searchLower = filters.search.toLowerCase();
          courses = courses.filter(
            (c) =>
              c.name.toLowerCase().includes(searchLower) ||
              c.code.toLowerCase().includes(searchLower)
          );
        }

        return {
          courses,
          total: courses.length,
          page: 1,
          pageSize: 20,
        };
      }

      const response = await apiClient.get<LegacyCoursesResponse>(getCoursesBasePath(), {
        params: { search: filters?.search },
      });
      return response.data;
    },
  });
}

const isMockMode = (): boolean =>
  String(import.meta.env.VITE_USE_MOCKS ?? '').toLowerCase() === 'true';

/** Mock GET /courses/:id for dev:mock and localhost fallback (recovery credit modal, etc.). */
async function getMockCourseDetail(courseId: string): Promise<CourseDetail> {
  const mockData = await import('../mock-data/course-details.json');
  const byId = mockData.default as unknown as Record<string, CourseDetail>;
  const normalizedId = courseId.trim();
  const matched = byId[normalizedId];
  if (matched) {
    return matched;
  }

  const catalogCourse = MOCK_COURSE_CATALOG.find(
    (course) => course.id.toLowerCase() === normalizedId.toLowerCase()
  );
  const baseName = catalogCourse?.name ?? normalizedId;

  return {
    id: normalizedId,
    name: baseName,
    subjectArea: catalogCourse?.subjectArea ?? { name: 'General', CSSCCourse: 0, SCEDSubject: 0 },
    credits: catalogCourse?.credits ?? 1,
    grades: catalogCourse?.grades ?? [9, 10, 11, 12],
    status: CourseStatus.ACTIVE,
    schools: catalogCourse?.schools ?? [],
    repeatable: catalogCourse?.repeatable ?? false,
    courseCreditRecovery: [
      [
        {
          id: `${normalizedId}-RV1`,
          identifier: `${normalizedId}-RV1`,
          name: `${baseName} — Recovery Option A`,
          credits: catalogCourse?.credits ?? 1,
          status: CourseStatus.ACTIVE,
          grades: catalogCourse?.grades ?? [9, 10, 11, 12],
        },
        {
          id: `${normalizedId}-RV2`,
          identifier: `${normalizedId}-RV2`,
          name: `${baseName} — Recovery Option B`,
          credits: catalogCourse?.credits ?? 1,
          status: CourseStatus.ACTIVE,
          grades: catalogCourse?.grades ?? [9, 10, 11, 12],
        },
      ],
    ],
  };
}

/**
 * Map from student plan requirement IDs to the department label used in courses.json.
 */
const REQUIREMENT_DEPARTMENT_MAP: Record<string, string> = {
  'req-eng': 'English',
  'req-sci': 'Science',
  'req-math': 'Mathematics',
  'req-wl': 'World Language',
};

/**
 * Fetch courses for a student plan requirement.
 * In mock mode: filters courses.json by department mapping.
 * In real mode: POST /courses/getCourses scoped to the plan's school.
 *
 * @param requirementId - The requirement ID (used as mock filter key)
 * @param search        - Optional search string
 * @param schoolId      - School ID from the student plan (e.g. "168215USPU");
 *                        query is held until this is defined.
 */
export function useGetCoursesForRequirement(
  requirementId: string | undefined,
  search?: string,
  schoolId?: string
): UseQueryResult<LegacyCoursesResponse, DefaultError> {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.courses.list({ requirementId, search, schoolId }),
    // Wait for the plan to load so we have the real school ID before fetching courses.
    enabled: Boolean(requirementId) && (isMockMode() || Boolean(schoolId)),
    queryFn: async (): Promise<LegacyCoursesResponse> => {
      const getFilteredMock = async (): Promise<LegacyCoursesResponse> => {
        const department = requirementId ? (REQUIREMENT_DEPARTMENT_MAP[requirementId] ?? '') : '';
        const mockData = await import('../mock-data/courses.json');
        let courses = (mockData.default.courses as LegacyCourse[]).filter(
          (c) => !department || c.department === department
        );
        if (search) {
          const lower = search.toLowerCase();
          courses = courses.filter(
            (c) => c.name.toLowerCase().includes(lower) || c.code.toLowerCase().includes(lower)
          );
        }
        return { courses, total: courses.length, page: 1, pageSize: courses.length };
      };

      if (isMockMode()) {
        return getFilteredMock();
      }

      try {
        // POST /courses/getCourses scoped to the plan's school.
        // CourseModel: { id (course code), name (display), credits, grades, status,
        //               subjectArea: { name }, instructionalLevel }
        const response = await apiClient.post<{
          data: Array<{
            id: string;
            name: string;
            credits: number;
            grades?: number[];
            status?: string;
            subjectArea?: { name?: string };
            instructionalLevel?: string;
          }>;
          total: number;
          currentPage?: number;
          pageSize?: number;
        }>(getCoursesPath('getCourses'), {
          school: schoolId,
          ...(search ? { search } : {}),
        });

        const raw = response.data;
        const courses: LegacyCourse[] = (raw.data ?? []).map((c) => ({
          id: c.id,
          code: c.id,
          name: c.name,
          credits: c.credits,
          grades: c.grades,
          status: c.status,
          department: c.subjectArea?.name ?? '',
          level:
            (c.instructionalLevel as LegacyCourse['level']) ??
            ('Standard' as LegacyCourse['level']),
          description: '',
          prerequisites: [],
          terms: [],
          isActive: c.status !== 'INACTIVE',
          createdAt: '',
          updatedAt: '',
        }));
        return {
          courses,
          total: raw.total,
          page: raw.currentPage ?? 1,
          pageSize: raw.pageSize ?? courses.length,
        };
      } catch {
        if (isLocalHostRuntime()) {
          return getFilteredMock();
        }
        throw new Error('Failed to load courses');
      }
    },
    retry: false,
    throwOnError: false,
  });
}

/**
 * Normalizes a raw course object from the API.
 * The `credits` field is sometimes returned as a string (e.g. "1.5", "2") by the
 * legacy PowerSchool data layer. Coerce it to a number so the UI always gets a
 * consistent type and the Credits column never shows "Invalid Number".
 */
function normalizeCourse(raw: Course): Course {
  const credits = typeof raw.credits === 'string' ? parseFloat(raw.credits as string) : raw.credits;
  return { ...raw, credits: isNaN(credits) ? 0 : credits };
}

function normalizeCourseDetail(raw: CourseDetail): CourseDetail {
  const credits = typeof raw.credits === 'string' ? parseFloat(raw.credits as string) : raw.credits;
  return {
    ...raw,
    credits: isNaN(credits) ? 0 : credits,
  };
}

type CoursesResponseRaw = CoursesResponse & { page?: number };

/**
 * Succeed / legacy getCourses payloads sometimes omit `totalPages` or use `page` instead of
 * `currentPage`. Without correct pagination metadata, infinite pickers stop after the first chunk.
 */
function normalizeCoursesResponse(raw: CoursesResponse): CoursesResponse {
  const rawAny = raw as CoursesResponseRaw;
  const data = (rawAny.data ?? []).map(normalizeCourse);
  const pageSize = rawAny.pageSize && rawAny.pageSize > 0 ? rawAny.pageSize : 25;
  const total = typeof rawAny.total === 'number' ? rawAny.total : data.length;
  const currentPage = rawAny.currentPage ?? rawAny.page ?? 1;
  let totalPages = rawAny.totalPages;
  if (typeof totalPages !== 'number' || !Number.isFinite(totalPages) || totalPages < 1) {
    totalPages = Math.max(1, Math.ceil(total / pageSize));
  }
  return {
    ...rawAny,
    data,
    total,
    currentPage,
    totalPages,
    pageSize,
  };
}

function getGetCoursesEndpoint(): string {
  return isEmbeddedInSucceed()
    ? getCoursePlannerGetCoursesPathQa()
    : getCoursesPath('getCoursesModern');
}

/** Mock catalog pagination — shared by list and infinite picker queries. */
function mockCourseCatalogPage(filters: CourseFilters): CoursesResponse {
  let list: Course[] = [...MOCK_COURSE_CATALOG];
  if (filters?.status === CourseStatus.ACTIVE) {
    list = list.filter((c) => c.status === CourseStatus.ACTIVE);
  }
  if (filters?.search) {
    const term = filters.search.toLowerCase();
    list = list.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.id.toLowerCase().includes(term) ||
        (c.subjectArea?.name ?? '').toLowerCase().includes(term)
    );
  }
  if (filters?.schools) {
    const schoolFilter = filters.schools.split(',').filter(Boolean);
    list = list.filter((c) => c.schools?.some((s) => schoolFilter.includes(s)));
  }
  const subjectAreaFilter =
    filters?.subjectAreas && filters.subjectAreas.length > 0
      ? filters.subjectAreas
      : filters?.subjectArea
        ? filters.subjectArea
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : null;
  if (subjectAreaFilter) {
    list = list.filter((c) => subjectAreaFilter.includes(c.subjectArea?.name ?? ''));
  }
  if (filters?.grades) {
    const gradeValues = filters.grades.split(',');
    list = list.filter((c) => c.grades?.some((g) => gradeValues.includes(String(g))));
  }
  const sortBy = filters?.sortBy ?? 'id';
  const sortOrder = filters?.sortOrder ?? 'asc';
  const mul = sortOrder === 'asc' ? 1 : -1;
  list.sort((a, b) => {
    let av: string | number;
    let bv: string | number;
    if (sortBy === 'id') {
      av = a.id;
      bv = b.id;
    } else if (sortBy === 'name') {
      av = a.name;
      bv = b.name;
    } else if (sortBy === 'subjectArea') {
      av = a.subjectArea?.name ?? '';
      bv = b.subjectArea?.name ?? '';
    } else if (sortBy === 'credits') {
      av = a.credits;
      bv = b.credits;
    } else if (sortBy === 'status') {
      av = a.status;
      bv = b.status;
    } else if (sortBy === 'schools') {
      av = a.schools?.length ?? 0;
      bv = b.schools?.length ?? 0;
    } else {
      av = String(a.id);
      bv = String(b.id);
    }
    if (typeof av === 'string' && typeof bv === 'string') {
      return mul * av.localeCompare(bv, undefined, { sensitivity: 'base' });
    }
    return mul * (av < bv ? -1 : av > bv ? 1 : 0);
  });
  const page = filters?.page ?? 1;
  const pageSize = filters?.pageSize ?? 25;
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;
  const data = list.slice(start, start + pageSize);
  return { data, total, currentPage: page, totalPages, pageSize };
}

/**
 * Single page of course catalog (POST getCourses). Used by grid and add-course pickers.
 */
export async function fetchCourseCatalogPage(
  apiClient: AxiosInstance,
  filters: CourseFilters
): Promise<CoursesResponse> {
  if (isMockMode()) {
    return mockCourseCatalogPage(filters);
  }
  const response = await apiClient.post<CoursesResponse>(getGetCoursesEndpoint(), {
    ...filters,
    ...(isEmbeddedInSucceed() && isSchoolUser() ? { school: getHighschoolId() } : {}),
  });
  return normalizeCoursesResponse(response.data);
}

/**
 * Fetch courses with optional filters (course catalog).
 * - When VITE_USE_MOCKS=true: returns mock data (no API call). Local/mock behaviour is unchanged.
 * - Otherwise: POST getCourses. In QA we use the same path as old c-p-ui (getCoursePlannerGetCoursesPathQa());
 *   locally we use getCoursesPath so the call goes direct to course-planner-service.
 */
export function useGetCourseCatalog(
  filters?: CourseFilters,
  options?: { enabled?: boolean; staleTime?: number }
): UseQueryResult<CoursesResponse, DefaultError> {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.courseCatalog.list(filters),
    enabled: options?.enabled ?? true,
    staleTime: options?.staleTime,
    queryFn: async (): Promise<CoursesResponse> => {
      return fetchCourseCatalogPage(apiClient, filters ?? {});
    },
    placeholderData: keepPreviousData,
    retry: false,
    throwOnError: false,
  });
}

/**
 * Default invalidation only refetches *active* queries. The catalog list is inactive while the user
 * is on add/edit, so returning to the grid would otherwise show stale cache until staleTime elapsed.
 */
function invalidateCourseCatalogListQueries(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: queryKeys.courseCatalog.lists(),
    refetchType: 'all',
  });
}

/** Page size for school-mapping / copy-from-course search mutation (large single page). */
const CATALOG_PICKER_PAGE_SIZE = 1000;

/**
 * Add-course relationship multiselects (Credit Recovery, Equivalent, Prerequisites, Co-requisites).
 * Larger page size reduces round-trips; infinite scroll still loads the rest when metadata is correct.
 */
const RELATIONSHIP_PICKER_PAGE_SIZE = 100;

function relationshipCatalogInfiniteQueryKey(searchTrim: string, pageSize: number) {
  return [
    ...queryKeys.courseCatalog.lists(),
    'infinite-picker',
    'p100-id-search',
    searchTrim,
    pageSize,
  ] as const;
}

function relationshipCatalogInfiniteNextPageParam(
  lastPage: CoursesResponse,
  lastPageParam: unknown,
  pageSize: number
): number | undefined {
  const page = typeof lastPageParam === 'number' ? lastPageParam : 1;
  const resolvedPageSize =
    lastPage.pageSize && lastPage.pageSize > 0 ? lastPage.pageSize : pageSize;
  const total = typeof lastPage.total === 'number' ? lastPage.total : 0;
  let totalPages = lastPage.totalPages;
  if (typeof totalPages !== 'number' || !Number.isFinite(totalPages) || totalPages < 1) {
    totalPages = Math.max(1, Math.ceil(total / resolvedPageSize));
  }
  if (page >= totalPages) {
    return undefined;
  }
  return page + 1;
}

/**
 * Infinite catalog for add/edit course relationship multiselects.
 * - **POST** `.../getCoursesModern` (embedded via succeed-api path or direct to c-p-s).
 * - **Browse** (empty picker filter after debounce): `{ page, pageSize, sortBy: 'id', sortOrder: 'asc' }`;
 *   more pages load on scroll (`fetchNextPage`).
 * - **Search** (non-empty after debounce): same + `{ search }` — **no** `status: ACTIVE` so results match
 *   the main catalog (inactive courses are still valid relationship targets).
 */
export function useInfiniteCourseCatalogPicker(
  debouncedSearch: string,
  options?: { enabled?: boolean }
) {
  const apiClient = useApiClient();
  const searchTrim = debouncedSearch.trim();
  const isSearchMode = searchTrim.length > 0;
  const pageSize = RELATIONSHIP_PICKER_PAGE_SIZE;

  return useInfiniteQuery({
    queryKey: relationshipCatalogInfiniteQueryKey(searchTrim, pageSize),
    initialPageParam: 1,
    enabled: options?.enabled ?? true,
    queryFn: async ({ pageParam }: { pageParam: number }): Promise<CoursesResponse> => {
      const base: CourseFilters = {
        page: pageParam,
        pageSize,
        sortBy: 'id',
        sortOrder: 'asc',
      };
      if (isSearchMode) {
        return fetchCourseCatalogPage(apiClient, {
          ...base,
          search: searchTrim,
        });
      }
      return fetchCourseCatalogPage(apiClient, base);
    },
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      relationshipCatalogInfiniteNextPageParam(lastPage, lastPageParam, pageSize),
    placeholderData: keepPreviousData,
    retry: false,
    throwOnError: false,
  });
}

/**
 * Prefetch browse-mode (empty search) catalog pages for relationship pickers so clearing a filter
 * after an invalid search does not briefly show an empty list while the default query runs.
 */
export function usePrefetchRelationshipCatalogBrowseWhenEnabled(enabled: boolean): void {
  const queryClient = useQueryClient();
  const apiClient = useApiClient();
  const pageSize = RELATIONSHIP_PICKER_PAGE_SIZE;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const queryKey = relationshipCatalogInfiniteQueryKey('', pageSize);
    if (queryClient.getQueryData(queryKey)) {
      return;
    }
    void queryClient.prefetchInfiniteQuery({
      queryKey,
      initialPageParam: 1,
      queryFn: async ({ pageParam }: { pageParam: number }): Promise<CoursesResponse> =>
        fetchCourseCatalogPage(apiClient, {
          page: pageParam,
          pageSize,
          sortBy: 'id',
          sortOrder: 'asc',
        }),
      getNextPageParam: (
        lastPage: CoursesResponse,
        _pages: CoursesResponse[],
        lastPageParam: unknown
      ) => relationshipCatalogInfiniteNextPageParam(lastPage, lastPageParam, pageSize),
    });
  }, [enabled, queryClient, apiClient, pageSize]);
}

/**
 * Fetch a single course by ID
 */
export function useGetCourse(
  courseId: string,
  enabled = true,
  schoolId?: string
): UseQueryResult<CourseDetail, DefaultError> {
  const apiClient = useApiClient();
  const basePath = getCoursesBasePath();

  return useQuery({
    queryKey: [...queryKeys.courseCatalog.detail(courseId), schoolId ?? ''],
    queryFn: async () => {
      if (isMockMode()) {
        return getMockCourseDetail(courseId);
      }

      try {
        const response = await apiClient.get<CourseDetail>(`${basePath}/${courseId}`, {
          ...(schoolId ? { params: { schoolId } } : {}),
        });
        return normalizeCourseDetail(response.data);
      } catch (error) {
        if (isLocalHostRuntime()) {
          return getMockCourseDetail(courseId);
        }
        throw error;
      }
    },
    enabled,
    retry: false,
    throwOnError: false,
  });
}

/**
 * Fetches course details for several IDs so callers can inspect catalog relationships.
 * @param courseIds - Catalog IDs to load.
 * @param enabled - Whether the detail queries should run.
 */
export function useGetCoursesByIds(courseIds: string[], enabled = true) {
  const apiClient = useApiClient();
  const basePath = getCoursesBasePath();

  return useQueries({
    queries: courseIds.map((courseId) => ({
      queryKey: [...queryKeys.courseCatalog.detail(courseId), ''],
      queryFn: async (): Promise<CourseDetail> => {
        if (isMockMode()) {
          return getMockCourseDetail(courseId);
        }

        try {
          const response = await apiClient.get<CourseDetail>(`${basePath}/${courseId}`);
          return normalizeCourseDetail(response.data);
        } catch (error) {
          if (isLocalHostRuntime()) {
            return getMockCourseDetail(courseId);
          }
          throw error;
        }
      },
      enabled: enabled && Boolean(courseId),
      retry: false,
      throwOnError: false,
    })),
  });
}

/**
 * Whether a course ID already exists (add-course validation). Matches c-p-ui debounced GET
 * `/course-planner/courses/:id/exists`. Backend may respond 404 when the ID is unused — treated as available.
 */
export function useCourseIdExists(
  debouncedCourseId: string,
  options?: { enabled?: boolean }
): UseQueryResult<boolean, DefaultError> {
  const apiClient = useApiClient();
  const id = debouncedCourseId.trim();
  const enabled = (options?.enabled ?? true) && id.length > 0;

  return useQuery({
    queryKey: queryKeys.courses.exists(id),
    enabled,
    queryFn: async (): Promise<boolean> => {
      if (isMockMode()) {
        return MOCK_COURSE_CATALOG.some((c) => c.id.toUpperCase() === id.toUpperCase());
      }
      try {
        const { data } = await apiClient.get<boolean>(getCourseExistsPath(id));
        return data === true;
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          return false;
        }
        throw err;
      }
    },
    staleTime: 30_000,
    retry: false,
    throwOnError: false,
  });
}

/**
 * Search courses by name or ID (e.g. school-mapping drawer “copy from course”).
 * Same **POST getCourses** contract as the catalog dashboard search, not GET `/courses/search`.
 */
export function useSearchCourses() {
  const apiClient = useApiClient();
  const getCoursesEndpoint = isEmbeddedInSucceed()
    ? getCoursePlannerGetCoursesPathQa()
    : getCoursesPath('getCoursesModern');

  return useMutation({
    mutationFn: async (search: string): Promise<Course[]> => {
      if (isMockMode()) {
        const term = (search || '').toLowerCase();
        const list = term
          ? MOCK_COURSE_CATALOG.filter(
              (c) => c.name.toLowerCase().includes(term) || c.id.toLowerCase().includes(term)
            )
          : [];
        return list.slice(0, CATALOG_PICKER_PAGE_SIZE);
      }
      const response = await apiClient.post<CoursesResponse>(getCoursesEndpoint, {
        page: 1,
        pageSize: CATALOG_PICKER_PAGE_SIZE,
        search: search || '',
        grades: '',
        ...(isEmbeddedInSucceed() && isSchoolUser() ? { school: getHighschoolId() } : {}),
      });
      return (response.data?.data ?? []).map(normalizeCourse);
    },
  });
}

/**
 * Search courses by name or ID filtered by grade (for mandated courses drawer).
 * POST getCourses with { page: 1, pageSize: 20, search, grades, subjectAreas }.
 */
export function useSearchCoursesWithGrades() {
  const apiClient = useApiClient();
  const getCoursesEndpoint = isEmbeddedInSucceed()
    ? getCoursePlannerGetCoursesPathQa()
    : getCoursesPath('getCoursesModern');

  return useMutation({
    mutationFn: async ({
      search,
      grades,
      subjectAreas,
    }: {
      search: string;
      grades?: string;
      subjectAreas?: string[];
    }): Promise<Course[]> => {
      if (isMockMode()) {
        const term = (search || '').toLowerCase();
        let list = term
          ? MOCK_COURSE_CATALOG.filter(
              (c) => c.name.toLowerCase().includes(term) || c.id.toLowerCase().includes(term)
            )
          : [];
        if (subjectAreas && subjectAreas.length > 0) {
          list = list.filter((c) => subjectAreas.includes(c.subjectArea?.name ?? ''));
        }
        return list.slice(0, 20);
      }
      const response = await apiClient.post<CoursesResponse>(getCoursesEndpoint, {
        page: 1,
        pageSize: 20,
        search: search || '',
        grades: grades ?? '',
        sortBy: 'status',
        sortOrder: 'asc',
        ...(subjectAreas && subjectAreas.length > 0 ? { subjectAreas } : {}),
      });
      return (response.data?.data ?? []).map(normalizeCourse);
    },
  });
}

/**
 * Create a new course
 */
export function useCreateCourse() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const basePath = getCoursesBasePath();

  return useMutation({
    mutationFn: async (payload: { course: Partial<CourseDetail> }) => {
      const body =
        isMockMode() || !isCourseCatalogWafDescriptionEncodingEnabled()
          ? payload
          : encodeWafSafeDescriptionsInRequestBody(payload);
      const response = await apiClient.post<CourseDetail>(basePath, body);
      return response.data;
    },
    onSuccess: () => {
      void invalidateCourseCatalogListQueries(queryClient);
    },
  });
}

/**
 * Update an existing course
 */
export function useUpdateCourse() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const basePath = getCoursesBasePath();

  return useMutation({
    mutationFn: async (payload: { course: Partial<CourseDetail> }) => {
      const body =
        isMockMode() || !isCourseCatalogWafDescriptionEncodingEnabled()
          ? payload
          : encodeWafSafeDescriptionsInRequestBody(payload);
      const response = await apiClient.put<CourseDetail>(basePath, body);
      return response.data;
    },
    onSuccess: (data, variables) => {
      void invalidateCourseCatalogListQueries(queryClient);
      // List invalidation alone does not refresh per-course detail cache; global staleTime is 5m,
      // so reopening edit would show the pre-save course until the detail query goes stale.
      const id = data?.id ?? variables.course?.id;
      if (id) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.courseCatalog.detail(id),
          refetchType: 'all',
        });
      }
    },
  });
}

/**
 * Delete a course
 */
export function useDeleteCourse() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const basePath = getCoursesBasePath();

  return useMutation({
    mutationFn: async (courseId: string) => {
      await apiClient.delete(`${basePath}/${courseId}`);
      return courseId;
    },
    onSuccess: (courseId) => {
      queryClient.removeQueries({ queryKey: queryKeys.courseCatalog.detail(courseId) });
      void invalidateCourseCatalogListQueries(queryClient);
    },
  });
}

/**
 * Bulk delete courses
 */
export function useBulkDeleteCourses() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const bulkDeletePath = getCoursesPath('bulk/delete');

  return useMutation({
    mutationFn: async (courseIds: string[]) => {
      const response = await apiClient.post<boolean>(bulkDeletePath, courseIds);
      return response.data;
    },
    onSuccess: () => {
      void invalidateCourseCatalogListQueries(queryClient);
    },
  });
}

/**
 * Bulk update course status
 */
export function useBulkUpdateCourseStatus() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const bulkStatusPath = getCoursesPath('bulk/status');

  return useMutation({
    mutationFn: async (payload: { courses: string[]; status: string }) => {
      const response = await apiClient.put<{ courses: string[]; status: string }>(
        bulkStatusPath,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      void invalidateCourseCatalogListQueries(queryClient);
    },
  });
}

export type ExportCourseType = 'catalog' | 'mapping';

/** Payload for course mapping export (POST). Matches legacy c-p-ui body (Succeed `dataExport` / bulk mapping). */
export interface ExportCourseMappingPayload {
  headers: { 'Content-Type': string };
  schools: unknown[];
}

/** Matches c-p-ui `dataExport.sagas` DATA_EXPORT_URL + export type strings. */
const DATA_EXPORT_URL = '/data-export';
const JOB_COMPLETED = 'COMPLETED';
const JOB_FAILED = 'FAILED';

/** Used on file download GETs and inside mapping-export POST JSON (`headers`), per legacy c-p-ui. */
const EXPORT_DOWNLOAD_HEADERS = { 'Content-Type': 'application/octet-stream' } as const;

type AsyncCourseExportType = 'exportCourseCatalogUI' | 'exportCourseMappingsUI';

interface DataExportJobStart {
  guid: string;
  status?: string;
}

function exportDelay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function normalizeExportJobStatus(data: unknown): string {
  let raw = '';
  if (typeof data === 'string') {
    raw = data;
  } else if (data && typeof data === 'object' && 'status' in data) {
    raw = String((data as { status: unknown }).status);
  }
  return raw.trim().toUpperCase();
}

/**
 * Poll job status (same cadence as c-p-ui `isExportComplete`: GET getStatus, then 4s delay while STARTED).
 */
async function pollDataExportUntilComplete(apiClient: AxiosInstance, guid: string): Promise<void> {
  const maxRounds = 450;
  for (let i = 0; i < maxRounds; i++) {
    const statusRes = await apiClient.get<string>(`${DATA_EXPORT_URL}/${guid}/getStatus`);
    const status = normalizeExportJobStatus(statusRes.data);
    if (status === JOB_COMPLETED) {
      return;
    }
    if (status === JOB_FAILED) {
      throw new Error('Export job failed');
    }
    await exportDelay(4000);
  }
  throw new Error('Export timed out');
}

/**
 * Mirrors staff `CoursePlanner/modules/shared/common.sagas` `download()`:
 * legacy does **not** set `responseType` — CSV is received as text and passed to `js-file-download`.
 * Forcing `responseType: 'blob'` here caused byte/encoding mismatches vs c-p-ui for the same job.
 */
function responseDataToExportBlob(data: unknown): Blob {
  if (data instanceof Blob) {
    return data;
  }
  if (typeof data === 'string') {
    return new Blob([data], { type: 'text/csv;charset=utf-8' });
  }
  if (data instanceof ArrayBuffer) {
    return new Blob([data]);
  }
  return new Blob([String(data ?? '')]);
}

async function downloadDataExportFile(apiClient: AxiosInstance, guid: string): Promise<Blob> {
  const response = await apiClient.get(`${DATA_EXPORT_URL}/${guid}/download`, {
    headers: EXPORT_DOWNLOAD_HEADERS,
  });
  return responseDataToExportBlob(response.data);
}

/**
 * c-p-ui async path: GET `/data-export/exportCoursesAsync/{exportCourseCatalogUI|exportCourseMappingsUI}`.
 */
async function exportCoursesThroughDataExportAsync(
  apiClient: AxiosInstance,
  asyncType: AsyncCourseExportType
): Promise<Blob> {
  const url = `${DATA_EXPORT_URL}/exportCoursesAsync/${asyncType}`;
  const { data: job } = await apiClient.get<DataExportJobStart>(url);
  if (!job?.guid) {
    throw new Error('Invalid export job response');
  }
  await pollDataExportUntilComplete(apiClient, job.guid);
  return downloadDataExportFile(apiClient, job.guid);
}

/** Reject blobs that look like JSON error bodies (can occur when responseType is blob). */
async function assertLikelySuccessfulExportBlob(blob: Blob): Promise<void> {
  if (blob.size === 0) {
    throw new Error('Empty export file');
  }
  const type = (blob.type || '').toLowerCase();
  if (type.includes('json')) {
    throw new Error('Export request failed');
  }
  const head = await blob.slice(0, Math.min(256, blob.size)).text();
  const trimmed = head.trimStart();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    throw new Error('Export request failed');
  }
}

/**
 * Export courses.
 * - **Course mapping:** embedded + LD `feature.coursePlanner.limitCoursesForListView` not false -> async
 *   `/data-export/exportCoursesAsync/exportCourseMappingsUI`; else direct
 *   `POST .../bulk/export-course-mapping-V1` with `{ headers, schools }`.
 * - **Course catalog (district):** embedded + LD `feature.coursePlanner.limitCoursesForListView` not false → async
 *   `exportCourseCatalogUI`; else direct `GET .../bulk/export`.
 * - **Course catalog (school staff):** direct `GET .../bulk/exportSchool` (school-scoped CSV on c-p-s; skips unscoped async job).
 * When VITE_USE_MOCKS=true returns a minimal CSV blob for both.
 */
export function useExportCourses() {
  const apiClient = useApiClient();
  const catalogExportPath = getCoursesPath('bulk/export');
  const catalogExportSchoolPath = getCoursesPath('bulk/exportSchool');
  const mappingExportPath = getCoursesPath('bulk/export-course-mapping-V1');

  return useMutation({
    mutationFn: async (
      options: ExportCourseType | { type: ExportCourseType; schools?: unknown[] }
    ): Promise<Blob> => {
      const exportType = typeof options === 'string' ? options : options.type;
      const schools = typeof options === 'object' && options.schools ? options.schools : [];

      if (isMockMode()) {
        const header =
          exportType === 'mapping'
            ? 'Course ID,Course Name,School,Mapped\n'
            : 'Course ID,Course Name,Subject Area,Status\n';
        return new Blob([header], { type: 'text/csv' });
      }

      if (exportType === 'mapping') {
        const useAsyncPipeline = isCourseCatalogExportAsyncEnabled();
        if (useAsyncPipeline) {
          const blob = await exportCoursesThroughDataExportAsync(
            apiClient,
            'exportCourseMappingsUI'
          );
          await assertLikelySuccessfulExportBlob(blob);
          return blob;
        }

        const mappingBody: ExportCourseMappingPayload = {
          headers: { ...EXPORT_DOWNLOAD_HEADERS },
          schools,
        };
        const response = await apiClient.post<Blob>(mappingExportPath, mappingBody, {
          responseType: 'blob',
        });
        const blob = response.data;
        await assertLikelySuccessfulExportBlob(blob);
        return blob;
      }

      if (isSchoolUser()) {
        const response = await apiClient.get<Blob>(catalogExportSchoolPath, {
          responseType: 'blob',
          headers: EXPORT_DOWNLOAD_HEADERS,
        });
        const blob = response.data;
        await assertLikelySuccessfulExportBlob(blob);
        return blob;
      }

      const useAsyncPipeline = isCourseCatalogExportAsyncEnabled();

      if (useAsyncPipeline) {
        const blob = await exportCoursesThroughDataExportAsync(apiClient, 'exportCourseCatalogUI');
        await assertLikelySuccessfulExportBlob(blob);
        return blob;
      }

      const response = await apiClient.get<Blob>(catalogExportPath, {
        responseType: 'blob',
        headers: EXPORT_DOWNLOAD_HEADERS,
      });
      const blob = response.data;
      await assertLikelySuccessfulExportBlob(blob);
      return blob;
    },
  });
}

/**
 * Bulk update course subject area. PUT bulk/subjectArea with { courses, subjectArea }.
 */
export function useBulkUpdateSubjectArea() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const bulkSubjectAreaPath = getCoursesPath('bulk/subjectArea');

  return useMutation({
    mutationFn: async (payload: { courses: string[]; subjectArea: string }) => {
      const response = await apiClient.put<{ courses: string[]; subjectArea: string }>(
        bulkSubjectAreaPath,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      void invalidateCourseCatalogListQueries(queryClient);
    },
  });
}

/**
 * Fetch all course IDs for current filters (for "Select all" across pages).
 * Mock: apply same filters + sort, return all IDs. Real API: paginate with pageSize 1000 and collect IDs.
 */
export function useFetchAllCourseIds() {
  const apiClient = useApiClient();
  const getCoursesEndpoint = isEmbeddedInSucceed()
    ? getCoursePlannerGetCoursesPathQa()
    : getCoursesPath('getCoursesModern');

  return useMutation({
    mutationFn: async ({
      filters,
      total,
    }: {
      filters: CourseFilters;
      total: number;
    }): Promise<string[]> => {
      if (total <= 0) {
        return [];
      }
      if (isMockMode()) {
        let list: Course[] = [...MOCK_COURSE_CATALOG];
        if (filters?.status === CourseStatus.ACTIVE) {
          list = list.filter((c) => c.status === CourseStatus.ACTIVE);
        }
        if (filters?.search) {
          const term = (filters.search ?? '').toLowerCase();
          list = list.filter(
            (c) =>
              c.name.toLowerCase().includes(term) ||
              c.id.toLowerCase().includes(term) ||
              (c.subjectArea?.name ?? '').toLowerCase().includes(term)
          );
        }
        if (filters?.schools) {
          const schoolFilter = filters.schools.split(',').filter(Boolean);
          list = list.filter((c) => c.schools?.some((s) => schoolFilter.includes(s)));
        }
        const subjectAreaFilter =
          filters?.subjectAreas && filters.subjectAreas.length > 0
            ? filters.subjectAreas
            : filters?.subjectArea
              ? filters.subjectArea
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
              : null;
        if (subjectAreaFilter) {
          list = list.filter((c) => subjectAreaFilter.includes(c.subjectArea?.name ?? ''));
        }
        if (filters?.grades) {
          const gradeValues = filters.grades.split(',');
          list = list.filter((c) => c.grades?.some((g) => gradeValues.includes(String(g))));
        }
        const sortBy = filters?.sortBy ?? 'id';
        const sortOrder = filters?.sortOrder ?? 'asc';
        const mul = sortOrder === 'asc' ? 1 : -1;
        list.sort((a, b) => {
          let av: string | number;
          let bv: string | number;
          if (sortBy === 'id') {
            av = a.id;
            bv = b.id;
          } else if (sortBy === 'name') {
            av = a.name;
            bv = b.name;
          } else if (sortBy === 'subjectArea') {
            av = a.subjectArea?.name ?? '';
            bv = b.subjectArea?.name ?? '';
          } else if (sortBy === 'credits') {
            av = a.credits;
            bv = b.credits;
          } else if (sortBy === 'status') {
            av = a.status;
            bv = b.status;
          } else if (sortBy === 'schools') {
            av = a.schools?.length ?? 0;
            bv = b.schools?.length ?? 0;
          } else {
            av = String(a.id);
            bv = String(b.id);
          }
          if (typeof av === 'string' && typeof bv === 'string') {
            return mul * av.localeCompare(bv, undefined, { sensitivity: 'base' });
          }
          return mul * (av < bv ? -1 : av > bv ? 1 : 0);
        });
        return list.map((c) => c.id);
      }
      const allIds: string[] = [];
      const pageSize = Math.min(total, 1000);
      let page = 1;
      let fetched = 0;
      while (fetched < total) {
        const res = await apiClient.post<CoursesResponse>(getCoursesEndpoint, {
          ...filters,
          page,
          pageSize,
        });
        const chunk = res.data.data.map((c) => c.id);
        allIds.push(...chunk);
        fetched += chunk.length;
        if (chunk.length < pageSize) {
          break;
        }
        page += 1;
      }
      return allIds;
    },
  });
}

/**
 * Legacy courseRepository object for backward compatibility
 */
export const courseRepository = {
  useGetCourses: useGetCourseCatalog,
  useGetCourse,
  useCreateCourse,
  useUpdateCourse,
  useDeleteCourse,
  useBulkDeleteCourses,
  useBulkUpdateCourseStatus,
  useBulkUpdateSubjectArea,
  useExportCourses,
  useFetchAllCourseIds,

  useGetCourseSections(
    courseId: string,
    enabled = true
  ): UseQueryResult<CourseSection[], DefaultError> {
    const apiClient = useApiClient();

    return useQuery({
      queryKey: queryKeys.courses.sections(courseId),
      queryFn: async () => {
        if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true') {
          const mockData = await import('../mock-data/sections.json');
          return mockData.default as CourseSection[];
        }

        const response = await apiClient.get<CourseSection[]>(`/courses/${courseId}/sections`);
        return response.data;
      },
      enabled,
    });
  },

  useSearchCourses(
    searchTerm: string,
    enabled = true
  ): UseQueryResult<LegacyCourse[], DefaultError> {
    const apiClient = useApiClient();

    return useQuery({
      queryKey: queryKeys.courses.search(searchTerm),
      queryFn: async () => {
        if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true') {
          const mockData = await import('../mock-data/courses.json');
          const courses = mockData.default.courses as LegacyCourse[];
          return courses.filter(
            (c) =>
              c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
              c.id.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }

        const response = await apiClient.get<LegacyCourse[]>('/courses/search', {
          params: { q: searchTerm },
        });
        return response.data;
      },
      enabled: enabled && searchTerm.length >= 2,
    });
  },
};
